import type { ReactNode } from 'react';
import { genreOptions, regionOptions, yearOptions } from '@/mocks/media';

export interface FilterState {
  genres: string[];
  region: string;
  year: string;
}

interface FilterSidebarProps {
  filter: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

function FilterGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="py-4 border-b border-background-200/70 last:border-b-0">
      <h3 className="text-sm font-semibold text-foreground-900 mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterSidebar({ filter, onChange, onReset }: FilterSidebarProps) {
  const toggleGenre = (genre: string) => {
    const next = filter.genres.includes(genre)
      ? filter.genres.filter((g) => g !== genre)
      : [...filter.genres, genre];
    onChange({ ...filter, genres: next });
  };

  const chipClass = (active: boolean) =>
    `px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap cursor-pointer border transition-colors ${
      active
        ? 'bg-primary-500 text-background-50 dark:text-foreground-950 border-primary-500'
        : 'bg-background-100 text-foreground-600 border-background-200 hover:border-primary-300 hover:text-primary-600'
    }`;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          重置
        </button>
      </div>

      <FilterGroup title="类型">
        {genreOptions.map((genre) => (
          <button
            key={genre}
            type="button"
            onClick={() => toggleGenre(genre)}
            className={chipClass(filter.genres.includes(genre))}
          >
            {genre}
          </button>
        ))}
      </FilterGroup>

      <FilterGroup title="地区">
        <button
          type="button"
          onClick={() => onChange({ ...filter, region: '' })}
          className={chipClass(filter.region === '')}
        >
          全部
        </button>
        {regionOptions.map((region) => (
          <button
            key={region}
            type="button"
            onClick={() => onChange({ ...filter, region })}
            className={chipClass(filter.region === region)}
          >
            {region}
          </button>
        ))}
      </FilterGroup>

      <FilterGroup title="年份">
        <button
          type="button"
          onClick={() => onChange({ ...filter, year: '' })}
          className={chipClass(filter.year === '')}
        >
          全部
        </button>
        {yearOptions.map((year) => (
          <button
            key={year}
            type="button"
            onClick={() => onChange({ ...filter, year })}
            className={chipClass(filter.year === year)}
          >
            {year}
          </button>
        ))}
      </FilterGroup>
    </div>
  );
}